import { useMutation } from "@apollo/client";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { gql } from "generated-graphql";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useEffect, useState } from "react";

const ADD_TASK_COMMENT = gql(`
  mutation AddTaskComment($input: AddTaskCommentInput!) {
    addTaskComment(input: $input) {
      ...CommentThread
    }
  }
`);

const DELETE_COMMENT = gql(`
  mutation DeleteCommentForTask($id: ID!) {
    deleteComment(id: $id)
  }
`);

type TaskCommentSectionProps = {
  virtualEventId?: string;
  comments: CommentThreadFragment[];
};

export function TaskCommentSection({
  virtualEventId,
  comments: incomingComments,
}: TaskCommentSectionProps) {
  const alerts = useAlerts();
  const { user } = useCurrentUser();
  const [comments, setComments] = useState<CommentThreadFragment[]>(incomingComments);

  useEffect(() => {
    setComments(incomingComments ?? []);
  }, [incomingComments]);

  const [addTaskComment, { loading: addingComment }] = useMutation(ADD_TASK_COMMENT, {
    onError: () => {
      alerts.error("Failed to add task comment");
    },
  });

  const [deleteComment, { loading: deletingComment }] = useMutation(DELETE_COMMENT, {
    onError: () => {
      alerts.error("Failed to delete task comment");
    },
  });

  const handleAddComment = async (message: string) => {
    if (!virtualEventId) {
      return;
    }

    const result = await addTaskComment({
      variables: {
        input: {
          virtualEventId,
          comment: message,
        },
      },
    });

    const newComment = result.data?.addTaskComment;
    if (newComment) {
      setComments((existing) => [...existing, newComment]);
      alerts.success("Comment added");
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment({
      variables: {
        id: commentId,
      },
    });

    setComments((existing) => existing.filter((comment) => comment.id !== commentId));
    alerts.success("Comment deleted");
  };

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ModeCommentOutlinedIcon fontSize="small" sx={{ color: "text.secondary", mt: "1px" }} />
        <Typography variant="h6">Comments</Typography>
      </Stack>

      {virtualEventId ? (
        <GenericCommentStream
          title=""
          comments={comments}
          currentUserId={user?.id}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          addLoading={addingComment}
          deleteLoading={deletingComment}
          placeholder="Add context, blockers, or handoff notes..."
          buttonText="Comment"
          submitButtonVariant="icon"
          inputRows={3}
          inputMaxRows={10}
          emptyStateMessage="No comments on this task yet."
        />
      ) : (
        <Box
          sx={{
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            border: (theme) => `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Comments are available after the task is created.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
